import React, { useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import "./App.scss";
import Activity from "Pages/Activity";
import MActivity from "Pages/mobile/MActivity";
import TestLink from "./pages/TestLink";
import MCart from "Pages/mobile/MCart";
import OrderPreview from "./pages/OrderPreview";
import MOrderPreview from "Pages/mobile/MOrderPreview";
import OrderSuccess from "./pages/OrderSuccess";
import SsoAuthorize from "./pages/SsoAuthorize";
import NotFound from "./pages/NotFound";
import { AppContext } from "Store";
import cookie from "js-cookie";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  NavigateFunction,
} from "react-router-dom";
import config, {
  customerActivityScopeUrl,
  customerUrl,
  isTestEnv,
  SESSION_STORAGE_KEY_MAP,
  sobotChannelId,
  sobotDomainPC,
  sobotSysnum,
  sobotUrlTestPC,
  stockCenterAdminLoginUrl,
  USER_TYPE,
  type UserType,
} from "./utils/constant";
import { getAuthToken, getLocation, isWechatMiniProgramEnv } from "./utils/common";
import { ConfigProvider, message } from "antd";
import axios from "axios";
import { AAInitUserDetails } from "./utils/analyticsUtils";
import { getSelectedSTObj } from "Utils/addressUtils";

const getActivityIdFromPath = (pathname?: string) => {
  if (!pathname) return "";
  const normalizedPath = pathname.replace(/^\/campaigns/, "");
  const pathSegments = normalizedPath.split("/").filter(Boolean);
  if (!pathSegments.length) {
    return "";
  }
  if (pathSegments[0] === "m") {
    if (pathSegments[1] === "myCart" || pathSegments[1] === "orderPreview") {
      return pathSegments[2] || "";
    }
    return pathSegments[1] || "";
  }
  if (pathSegments[0] === "orderPreview") {
    return pathSegments[1] || "";
  }
  if (
    pathSegments[0] === "success" ||
    pathSegments[0] === "testlink" ||
    pathSegments[0] === "sso"
  ) {
    return "";
  }
  return pathSegments[0];
};

const shouldDeferCurrentSTCleanup = (pathname?: string) => {
  if (!pathname) return false;
  const normalizedPath = pathname.replace(/^\/campaigns/, "");
  const pathSegments = normalizedPath.split("/").filter(Boolean);

  if (!pathSegments.length) {
    return false;
  }

  if (pathSegments[0] === "m") {
    return pathSegments.length === 2;
  }

  return pathSegments.length === 1;
};

const getAllShiptoByAccount = (customerData: any) => {
  const shipToList = customerData?.allShipToBillToAddresses;
  if (!Array.isArray(shipToList)) {
    return [];
  }

  return Array.from(
    new Set(
      shipToList
        .map((item: any) => String(item?.shipTo?.accountNumber ?? "").trim())
        .filter(Boolean)
    )
  );
};

const markBlockedShipTos = (customerData: any, customerActivityScopeResp: any) => {
  const scopeList = Array.isArray(customerActivityScopeResp)
    ? customerActivityScopeResp
    : Array.isArray(customerActivityScopeResp?.data)
      ? customerActivityScopeResp.data
      : [];

  if (!Array.isArray(customerData?.allShipToBillToAddresses) || !scopeList.length) {
    return customerData;
  }

  const blockedLoginAccounts = new Set(
    scopeList
      .filter(
        (activityScopeObj: any) =>
          activityScopeObj?.customerScope === "Blocked" &&
          activityScopeObj?.customerDataType === "ST" &&
          activityScopeObj?.loginAccount
      )
      .map((activityScopeObj: any) => String(activityScopeObj.loginAccount))
  );

  const selectedLoginAccounts = new Set(
    scopeList
      .filter(
        (activityScopeObj: any) =>
          activityScopeObj?.customerScope === "Selected" &&
          activityScopeObj?.customerDataType === "ST" &&
          activityScopeObj?.loginAccount
      )
      .map((activityScopeObj: any) => String(activityScopeObj.loginAccount))
  );

  console.log("selectedLoginAccounts", selectedLoginAccounts);
  
  if (!selectedLoginAccounts.size && !blockedLoginAccounts.size) {
    return customerData;
  }

  return {
    ...customerData,
    allShipToBillToAddresses: customerData.allShipToBillToAddresses.map(
      (item: any) => {
        const accountNumber = item?.shipTo?.accountNumber;
        const normalizedAccountNumber = String(accountNumber ?? "");
        console.log("normalizedAccountNumber", normalizedAccountNumber);

        if (selectedLoginAccounts.size > 0) {
          const disabled =
            !accountNumber ||
            !selectedLoginAccounts.has(normalizedAccountNumber) ||
            item?.shipTo?.addressStatus === "PENDING";
          if (!disabled && !item?.shipTo?.disabled) {
            return item;
          }

          return {
            ...item,
            shipTo: {
              ...item.shipTo,
              disabled,
            },
          };
        }

        if (!accountNumber) {
          return item;
        }

        if (!blockedLoginAccounts.has(normalizedAccountNumber)) {
          return item;
        }


        return {
          ...item,
          shipTo: {
            ...item.shipTo,
            disabled: true,
          },
        };
      }
    ),
  };
};


// chat bot
(function (w: any, d, e) {
  const prev = d.querySelector("#zhichiScript");
  prev && d.body.removeChild(prev);
  w[e] = function () {
    w.cbk = w.cbk || [];
    w.cbk.push(arguments);
  };
  let x = d.createElement("script");
  x.async = true;
  x.id = "zhichiScript";
  x.className = "zhiCustomBtn";

  x.src = isTestEnv ? sobotUrlTestPC : `https://${sobotDomainPC}.soboten.com/chat/frame/v6/entrance.js?sysnum=${sobotSysnum}`;
  d.body.appendChild(x);
})(window, document, "zc");
// @ts-ignore
zc("config", {
  auto_expand: false,
  channelid: sobotChannelId,
  custom: true,
  refresh: true,
  re_render: true,
});

function App() {
  const [loginState, setLoginState] = React.useState<any>({
    status: "idle",
    token: "",
  }); // replace null with your initial state
  const [refreshCart, setRefreshCart] = React.useState<number>(1);
  const [checkedCartItems, setCheckedCartItems] = React.useState(new Set());
  const [selectedAddressKey, setSelectedAddressKey] = React.useState("");
  const [cartData, setCartData] = React.useState({
    activity: null,
    exchange: null,
    total: "",
    originalTotal: "",
    savingTotal: "",
    matchStatus: false,
  });
  const [activityPermissionMeta, setActivityPermissionMeta] = React.useState<{
    activityId?: string;
    customerDataType?: string;
  }>({
    activityId: "",
    customerDataType: undefined,
  });
  const [cartCount, setCartCount] = React.useState<number>(0);
  const isEmployee = loginState?.userType === USER_TYPE.EMPLOYEE;
  useEffect(() => {
    console.log("loginState", loginState.status);
    if (loginState.status === "idle" || loginState.status === "fetching")
      return;
    const curToken = loginState.token;
    axios.interceptors.request.use(
      (config) => {
        console.log(" loginState.status Request was sent", loginState.status);
        config.headers.Authorization = curToken;
        console.log("reset header token", curToken);
        return config;
      },
      (error) => {
        // Handle the request error
        console.log("An error occurred while making the request", error);
        return Promise.reject(error);
      }
    );
    axios.interceptors.response.use(
      (response) => {
        console.log("Response was received");
        console.log("response", response);
        const token =
          response.headers["authorization"] ||
          response.headers["Authorization"];
        if (!token || token === "undefined") {
          //  do nothing
        } else {
          console.log("did refresh token");
          loginState.token = token;
          if (isEmployee) {
            sessionStorage.setItem(SESSION_STORAGE_KEY_MAP.AUTH_TOKEN, token);
          } else {
            localStorage.setItem("authToken", token);
            cookie.set("token", token, { secure: true, sameSite: "strict" });
          }
        }
        return response;
      },
      (error) => {
        // Do something with the response error
        console.log("An error occurred while making the request");
        const { response } = error;
        if (response && response.status === 401) {
          console.log("response", response);
          console.log("401 error");
          message.error("您的登录已过期，请重新登录");
          localStorage.removeItem("authToken");
          cookie.remove("token");
          setLoginState({
            status: "failed",
            token: null,
            user: null,
          });
          if (sessionStorage.getItem(SESSION_STORAGE_KEY_MAP.AUTH_TOKEN)) {
            sessionStorage.removeItem(SESSION_STORAGE_KEY_MAP.AUTH_TOKEN);
            window.location.href = stockCenterAdminLoginUrl;
          }
        }
        return Promise.reject(error);
      }
    );
    console.log("removeItem currentCampaignId");
    localStorage.removeItem("currentCampaignId");
  }, [loginState]);
  // useEffect(() => {
  //   // chat bot
  //   (function (w: any, d, e) {
  //     const prev = d.querySelector("#zhichiScript");
  //     prev && d.body.removeChild(prev);
  //     w[e] = function () {
  //       w.cbk = w.cbk || [];
  //       w.cbk.push(arguments);
  //     };
  //     let x = d.createElement("script");
  //     x.async = true;
  //     x.id = "zhichiScript";
  //     x.className = "zhiCustomBtn";

  //     x.src = `https://${sobotDomainPC}.soboten.com/chat/frame/v6/entrance.js?sysnum=${sobotSysnum}`;
  //     d.body.appendChild(x);
  //   })(window, document, "zc");
  //   // @ts-ignore
  //   zc("config", {
  //     auto_expand: false,
  //     channelid: sobotChannelId,
  //     custom: true,
  //     refresh: true,
  //     re_render: true,
  //   });
  // });

  const refreshCartData = () => {
    setRefreshCart((refreshCart) => refreshCart + 1);
  };
  const refetchCustomerDetails = () => {
    return fetchProfile(loginState.token);
  };
  const fetchCustomerActivityScope = async (
    authToken: string,
    activityId?: string,
    allShiptoByAccount: string[] = []
  ) => {
    if (!activityId || !allShiptoByAccount.length) {
      return null;
    }
    return fetch(customerActivityScopeUrl, {
      method: "POST",
      headers: {
        Authorization: authToken,
        Language: "zh-CN",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        activityId: Number(activityId),
        allShiptoByAccount,
      }),
    }).then((res) => res.json());
  };
  const fetchProfile = async (
    authToken: string,
    options?: any,
    userType?: UserType,
  ) => {
    const isEmployee = userType === USER_TYPE.EMPLOYEE;
    setLoginState({
      status: "fetching",
      token: authToken,
      user: null,
      userType: userType,
    });
    const activityId =
      getActivityIdFromPath(options?.pathname) ||
      getActivityIdFromPath(window.location.pathname);
    
    return fetch(customerUrl, {
      method: "GET",
      headers: {
        Authorization: authToken,
        Language: "zh-CN",
      },
    })
      .then((res) => res.json())
      .then(async (res) => {
        if (res.status === "success") {
          const allShiptoByAccount = getAllShiptoByAccount(res.data);
          if (!isEmployee) {
            localStorage.setItem("authToken", authToken);
            cookie.set("token", authToken, { secure: true, sameSite: "strict" });
          }
          let customerActivityScopeResp = null;
          try {
            customerActivityScopeResp = await fetchCustomerActivityScope(
              authToken,
              activityId,
              allShiptoByAccount
            );
            console.log("customerActivityScopeResp", customerActivityScopeResp);
          } catch (scopeError) {
            console.log("customerActivityScope error", scopeError);
          }

          const customerData = markBlockedShipTos(
            res.data,
            customerActivityScopeResp
          );
          console.log("fetchProfile res.data", customerData);
          const loginState = {
            status: "login",
            token: authToken,
            user: customerData,
            userType: userType,
            proxyUser: customerData?.proxyUser,
          };
          if (customerData?.user && !customerData?.user?.userId) {
            loginState.user.user.userId = customerData?.user?.userName;
          }
          
          setLoginState(loginState);
          if (!isEmployee) {
            const st = getSelectedSTObj(loginState);
            setSelectedAddressKey(st?.addressKey.toString() || "");
          }
          // clear the code of query params from the url
          if (options?.clearCode) {
            const search = localStorage.getItem("search");
            if (search) {
              console.log("navigate with search", search);
              // reload
              const reloadUrl = `${window.location.protocol}//${
                window.location.host
              }/campaigns${options.pathname}${decodeURIComponent(search)}`;
              console.log("reloadUrl", reloadUrl);
              // window.location.href = reloadUrl; works but will flash
              options.navigate(
                {
                  pathname: options.pathname,
                  search: decodeURIComponent(search) + "&refresh=true",
                },
                { replace: true }
              );
            } else {
              options.navigate(
                { pathname: options.pathname },
                { replace: true }
              );
            }
          }
        } else {
          setLoginState({
            status: "failed",
            token: null,
            user: null,
          });
        }
      })
      .catch((e) => {
        setLoginState({
          status: "failed",
          token: null,
          user: null,
        });
      });
  };

  return (
    <AppContext.Provider
      value={{
        loginState,
        setLoginState,
        refreshCart,
        refreshCartData,
        checkedCartItems,
        setCheckedCartItems,
        cartData,
        setCartData,
        activityPermissionMeta,
        setActivityPermissionMeta,
        cartCount,
        setCartCount,
        selectedAddressKey,
        setSelectedAddressKey,
        refetchCustomerDetails,
      }}
    >
      <ConfigProvider
        theme={{
          hashed: false,
          token: {
            colorPrimary: "#0071d0",
          },
          components: {
            Pagination: {
              itemActiveBg: "#e71316",
            },
            Table: {
              headerBg: "#fff",
              headerSplitColor: "#fff",
              borderColor: "#e5e5e5",
            },
          },
        }}
      >
        <Router basename="/campaigns">
          <AppBody fetchProfile={fetchProfile} />
        </Router>
      </ConfigProvider>
    </AppContext.Provider>
  );
}
const AppBody = (props: any) => {
  const location = window.location; // get the current location
  const query = new URLSearchParams(location.search); // parse the query parameters
  const navigate: NavigateFunction = useNavigate();
  const { loginState, setLoginState } = React.useContext(AppContext);
  const { fetchProfile } = props;

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.has("currentST") || shouldDeferCurrentSTCleanup(location.pathname)) {
      return;
    }
    searchParams.delete("currentST");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (location.pathname.endsWith("/sso/authorize")) {
      return;
    }
    if (loginState.status === "fetching") {
      return;
    }
    if (loginState.status !== "idle") {
      if (loginState.status === "login") {
        AAInitUserDetails(loginState.user);
      }
      if (loginState.status === "failed") {
        AAInitUserDetails(null);
      }
      return;
    }
    const code = query.get("code");
    let redirectUrl = query.get("redirect_url");
    const cid = query.get("cid");

    if (redirectUrl && redirectUrl.endsWith('/')) {
      console.log("redirectUrl found, redirecting...");
      redirectUrl = redirectUrl.slice(0, -1);
    }

    const sccToken = sessionStorage.getItem(SESSION_STORAGE_KEY_MAP.AUTH_TOKEN);
    if (sccToken) {
      fetchProfile(sccToken, undefined, USER_TYPE.EMPLOYEE);
      return;
    }

    if (code) {
      console.log("loginstep 1 back to index with code");
      fetchToken(code, redirectUrl ?? "", cid ?? "");
    } else {
      if (window.location.href.includes("testLink")) {
        console.log("testlink");
        return;
      }
      const authToken = localStorage.getItem("authToken");
      if (authToken) {
        try {
          const decodedToken: any = jwtDecode(authToken);
          const currentTime = Date.now() / 1000;
          // Check if the token is expired
          if (decodedToken.exp < currentTime) {
            console.log("Token is expired");
            setLoginState({
              status: "failed",
              token: null,
              user: null,
            });
          } else {
            fetchProfile(authToken);
          }
        } catch (error) {
          console.log("Token is invalid");
          setLoginState({
            status: "failed",
            token: null,
            user: null,
          });
        }
      } else {
        setLoginState({
          status: "failed",
          token: null,
          user: null,
        });
        cookie.remove("token");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginState, query]);
  const fetchToken = async (code: string, redirectUrl: string, cid: string) => {
    console.log("loginstep 2 users/login");
    setLoginState({
      status: "fetching",
      token: null,
      user: null,
    });
    let duration;
    try {
      // if (loginInitTimeStamp) {
      //   duration = new Date().getTime() - Number(loginInitTimeStamp)
      //   setLoginInitTimeStamp('')
      // }
    } catch (err) {
      console.log(err);
    }
    const data = await fetch(`${config.API_ENDPOINT}/users/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Source: isWechatMiniProgramEnv() ? "miniprogram" : "non-mini",
      },
      body: JSON.stringify({
        code,
        redirectUrl: cid ? `${redirectUrl}?cid=${cid}` : redirectUrl,
        duration,
      }),
    })
      .then((res) => {
        if (res.status === 200) {
          return res.json();
        } else {
          console.log("Fetching error", location.pathname);
          throw Error("Fetching error");
        }
      })
      .catch((err) => {
        // showToast({
        //   message: 'Login was unsuccessful.',
        //   delay: 3000,
        //   callback: () => {
        //     // console.log("Execution complete...");
        //   },
        // });
        setLoginState({
          status: "failed",
          token: null,
          user: null,
        });
        console.log(err);
      });
    console.log("loginstep fetchToken", data);
    const authToken = data?.data?.token;
    if (authToken) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete("code");
      // navigate({ pathname: location.pathname, search: searchParams.toString() });
      try {
        const pathV = "/" + location.pathname.split("/").slice(2).join("/");
        console.log("pathV", pathV);
        fetchProfile(authToken, {
          navigate,
          pathname: pathV,
          search: searchParams.toString(),
          clearCode: true,
        });
      } catch (error) {
        console.log("parser location error");
      }
    } else {
      setLoginState({
        status: "failed",
        token: null,
        user: null,
      });
    }
  };

  return (
    <div className="App">
      <Routes>
        <Route path="/:activityId" element={<Activity />} />
        <Route path="/m/:activityId" element={<MActivity />} />
        <Route path="/sso/authorize" element={<SsoAuthorize />} />
        <Route path="/testlink" element={<TestLink />} />
        <Route path="/m/myCart/:activityId" element={<MCart />} />
        <Route path="/orderPreview/:activityId" element={<OrderPreview />} />
        <Route path="/m/orderPreview/:activityId" element={<MOrderPreview />} />
        <Route path="/success" element={<OrderSuccess />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

export default App;
